import { ScrollArea } from './ScrollArea';
import { useInfiniteScroll } from '@ais-ui/custom-hooks';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Custom hook to reliably get the viewport element from a Radix ScrollArea
 * Handles timing issues where the viewport might not be immediately available
 */
const useRadixScrollAreaViewport = (
  onViewportReady?: (viewport: HTMLDivElement) => void
) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Effect to find and set the viewport when the scroll area is mounted
  useEffect(() => {
    const scrollAreaElement = scrollAreaRef.current;
    if (!scrollAreaElement) return;

    const findViewport = () => {
      const viewport = scrollAreaElement.querySelector(
        '[data-radix-scroll-area-viewport]'
      ) as HTMLDivElement;

      if (viewport && viewport !== viewportRef.current) {
        viewportRef.current = viewport;
        onViewportReady?.(viewport);
        return true;
      }
      return false;
    };

    // Try to find viewport immediately
    if (!findViewport()) {
      // If not found, observe for DOM changes
      const observer = new MutationObserver(() => {
        if (findViewport()) {
          observer.disconnect();
        }
      });

      observer.observe(scrollAreaElement, {
        childList: true,
        subtree: true,
      });

      // Cleanup observer
      return () => observer.disconnect();
    }
  }, [onViewportReady]);

  return {
    scrollAreaRef,
    viewportRef: viewportRef.current,
  };
};

type ScrollableListProps<T> = {
  items: T[] | undefined | null;
  renderItem: (item: T) => React.ReactNode;
  limit: number;
  observeResize: boolean;
  className?: string;
  onScrollViewportRef?: (ref: HTMLDivElement | null) => void;
  filterFn?: (item: T, search: string) => boolean;
  sortFn?: (a: T, b: T) => number;
};

export const InfiniteScroll = <T,>({
  items,
  renderItem,
  limit,
  observeResize,
  className,
  onScrollViewportRef,
  filterFn,
  sortFn,
  search,
}: ScrollableListProps<T> & { search?: string }) => {
  const { viewportRef, offset } = useInfiniteScroll(limit);

  // Handle Radix ScrollArea viewport detection reliably
  const handleViewportReady = useCallback(
    (viewport: HTMLDivElement) => {
      // Call the viewportRef callback to attach scroll listeners
      // viewportRef is a callback function, not a ref object
      viewportRef(viewport);

      // Also notify parent component if they need the viewport ref
      onScrollViewportRef?.(viewport);
    },
    [viewportRef, onScrollViewportRef]
  );

  const { scrollAreaRef } = useRadixScrollAreaViewport(handleViewportReady);

  const visibleItems = useMemo(() => {
    if (!items) return [];

    let result = [...items];

    // Apply search filter if provided
    if (filterFn && search) {
      result = result.filter((item) => filterFn(item, search));
    }

    // Apply sorting if provided
    if (sortFn) {
      result.sort(sortFn);
    }

    // Return items up to current offset + limit for infinite scroll
    return result.slice(0, offset + limit);
  }, [items, search, offset, limit, filterFn, sortFn]);

  return (
    <ScrollArea
      ref={scrollAreaRef}
      className={className}
      observeResize={observeResize}
    >
      {visibleItems.map((item, index) => {
        const renderedItem = renderItem(item);

        // Extract key from rendered item if it exists, otherwise use index
        const itemKey =
          React.isValidElement(renderedItem) && renderedItem.key
            ? renderedItem.key
            : index;

        return <React.Fragment key={itemKey}>{renderedItem}</React.Fragment>;
      })}
    </ScrollArea>
  );
};
